/**
 * Shared helpers for the Salvation Team console: React Query keys, paths, API error copy, dates and times, colours,
 * the welcome message merge fields and small text helpers.
 * All wording is South African English without long dashes (member-facing rule).
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { SALVATION_ERROR_CODES, SALVATION_INTAKE_CHANNEL } from '@actschurch/ccms-sdk';
import type { SalvationIntakeQuery, SalvationPeopleQuery } from '@actschurch/ccms-sdk';

dayjs.extend(relativeTime);

// ---------------------------------------------------------------- query keys

export const salvationKeys = {
  all: ['salvation'] as const,
  lookups: () => [...salvationKeys.all, 'lookups'] as const,
  settings: () => [...salvationKeys.all, 'settings'] as const,
  overviewRoot: () => [...salvationKeys.all, 'overview'] as const,
  overview: (teamId?: number | null) => [...salvationKeys.all, 'overview', teamId ?? 'all'] as const,
  people: (query?: SalvationPeopleQuery) => [...salvationKeys.all, 'people', query ?? {}] as const,
  peopleRoot: () => [...salvationKeys.all, 'people'] as const,
  person: (id: number) => [...salvationKeys.all, 'person', id] as const,
  teams: () => [...salvationKeys.all, 'teams'] as const,
  campuses: () => [...salvationKeys.all, 'campuses'] as const,
  membersRoot: () => [...salvationKeys.all, 'members'] as const,
  members: (teamId: number) => [...salvationKeys.all, 'members', teamId] as const,
  allocation: (teamId: number) => [...salvationKeys.all, 'allocation', teamId] as const,
  intakeRoot: () => [...salvationKeys.all, 'intake'] as const,
  intake: (query: SalvationIntakeQuery) => [...salvationKeys.all, 'intake', query] as const,
  intakeSources: () => [...salvationKeys.all, 'intake-sources'] as const,
  sourceOptions: (kind: string, q: unknown) => [...salvationKeys.all, 'source-options', kind, q] as const,
};

// ---------------------------------------------------------------- paths

export const SALVATION_BASE_PATH = '/app/salvation';

export const salvationPaths = {
  overview: SALVATION_BASE_PATH,
  people: `${SALVATION_BASE_PATH}/people`,
  person: (id: number) => `${SALVATION_BASE_PATH}/people/${id}`,
  /** The New decisions page; `bring` opens Bring in decisions straight away. */
  intake: (tab?: string, bring?: boolean) => {
    const p = new URLSearchParams();
    if (tab && tab !== 'waiting') p.set('tab', tab);
    if (bring) p.set('bring', '1');
    const q = p.toString();
    return `${SALVATION_BASE_PATH}/intake${q ? `?${q}` : ''}`;
  },
  teams: `${SALVATION_BASE_PATH}/teams`,
  team: (id: number, tab?: string) => `${SALVATION_BASE_PATH}/teams/${id}${tab && tab !== 'members' ? `?tab=${tab}` : ''}`,
  settings: (tab?: string) => `${SALVATION_BASE_PATH}/settings${tab && tab !== 'general' ? `?tab=${tab}` : ''}`,
  member: (memberId: number) => `/admin/members/${memberId}`,
};

// ---------------------------------------------------------------- errors

/** The machine code the API puts in errors[0] (SALVATION_ERROR_CODES), if any. */
export function salvationErrorCode(err: unknown): string | undefined {
  const e = err as { errors?: unknown } | null;
  if (e && Array.isArray(e.errors) && typeof e.errors[0] === 'string') return e.errors[0] as string;
  return undefined;
}

/** HTTP status of an SDK error, if any. */
export function salvationErrorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; statusCode?: number } | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

/** The possible duplicate a 409 duplicate_convert points at ("convertId:N" in errors[1]). */
export function duplicateConvertId(err: unknown): number | undefined {
  const e = err as { errors?: unknown } | null;
  if (!e || !Array.isArray(e.errors)) return undefined;
  const ref = (e.errors as unknown[]).find((x): x is string => typeof x === 'string' && x.startsWith('convertId:'));
  const id = ref ? Number(ref.slice('convertId:'.length)) : NaN;
  return Number.isFinite(id) ? id : undefined;
}

/** 503 member_service_unavailable: worth trying again in a moment. */
export function isMemberServiceUnavailable(err: unknown): boolean {
  return salvationErrorCode(err) === SALVATION_ERROR_CODES.MemberServiceUnavailable || salvationErrorStatus(err) === 503;
}

const ERROR_COPY: Record<string, string> = {
  [SALVATION_ERROR_CODES.NoAccess]: "You don't have access to do that.",
  [SALVATION_ERROR_CODES.NotYourCampus]: 'That campus is not one you lead.',
  [SALVATION_ERROR_CODES.ConvertNotFound]: 'That person could not be found.',
  [SALVATION_ERROR_CODES.TeamNotFound]: 'That campus team could not be found.',
  [SALVATION_ERROR_CODES.AssignmentPermanent]: 'Their Lead and Backup are permanent. Use Correct the assignment, with a reason, to change them.',
  [SALVATION_ERROR_CODES.SameLeadAndBackup]: 'The Lead and the Backup must be two different people.',
  [SALVATION_ERROR_CODES.NotEnoughTeam]: 'The campus team needs at least two active members first.',
  [SALVATION_ERROR_CODES.NotInFollowUp]: 'This person is past follow-up. Add a note, or reopen them first.',
  [SALVATION_ERROR_CODES.InvalidPhone]: 'That phone number does not look right. Use a number like 082 123 4567.',
  [SALVATION_ERROR_CODES.ContactRequired]: 'Enter a phone number or an email address.',
  [SALVATION_ERROR_CODES.Stale]: 'Someone else changed this a moment ago. Reload and try again.',
  [SALVATION_ERROR_CODES.MemberServiceUnavailable]: 'The member service is busy. Try again in a moment.',
};

/** Friendly copy for an API error: the code's copy, else the server message with its field details. */
export function salvationErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const code = salvationErrorCode(err);
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  const status = salvationErrorStatus(err);
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That couldn't be found. It may have been removed, or this part of the system is still being set up.";
  const e = err as { message?: string; errors?: unknown };
  const details = Array.isArray(e.errors)
    ? (e.errors as unknown[]).filter((x): x is string => typeof x === 'string' && !/^[a-z_]+$/.test(x) && !x.startsWith('convertId:'))
    : [];
  const base = e.message || fallback;
  return details.length ? `${base}: ${details.join('; ')}` : base;
}

// ---------------------------------------------------------------- dates

/** ISO instant to "3 Nov 2026, 14:05" in the browser's time zone. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('D MMM YYYY, HH:mm') : '';
}

/** "yyyy-MM-dd" to "3 Nov 2026". */
export function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const d = dayjs(day);
  return d.isValid() ? d.format('D MMM YYYY') : day;
}

/** ISO instant to "2 hours ago" / "in 3 days". */
export function relative(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.fromNow() : '';
}

/** True when the instant is in the past. */
export function isPast(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = dayjs(iso);
  return d.isValid() && d.isBefore(dayjs());
}

/** Today as "yyyy-MM-dd" (browser time). */
export function todayYmd(): string {
  return dayjs().format('YYYY-MM-DD');
}

/** A Date (DatePickerInput) as "yyyy-MM-dd". */
export function dateToYmd(d: Date | string | null | undefined): string | null {
  if (!d) return null;
  const v = dayjs(d);
  return v.isValid() ? v.format('YYYY-MM-DD') : null;
}

/** 2359 to "23:59". */
export function hhmmToTime(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return `${String(Math.floor(value / 100)).padStart(2, '0')}:${String(value % 100).padStart(2, '0')}`;
}

/** "23:59" (or "23:59:00") to 2359; null when it is not a time. */
export function timeToHhmm(value: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec((value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? h * 100 + min : null;
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const DAY_OPTIONS = DAY_NAMES.map((d, i) => ({ value: String(i), label: d }));

// ---------------------------------------------------------------- text

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-ZA')} ${n === 1 ? one : many}`;

/** "Thandi, Sipho and Lerato" (no Oxford comma). */
export function joinNames(names: string[]): string {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** Saves a Blob the API returned. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// ---------------------------------------------------------------- colours

/** Mantine colour names for the colour pickers on decision sources and milestones. */
export const COLOUR_OPTIONS = ['blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange', 'red', 'pink', 'grape', 'violet', 'indigo', 'gray', 'dark'];

export function colourOptions(current?: string | null) {
  return [...COLOUR_OPTIONS, ...(current && !COLOUR_OPTIONS.includes(current) ? [current] : [])].map((c) => ({ value: c, label: c }));
}

/** A colour from the API as a CSS colour (charts, dots): Mantine names become theme variables, hex stays hex. */
export function cssColour(c: string | null | undefined, fallback = 'gray'): string {
  const v = (c || '').trim() || fallback;
  if (/^(#|rgb|hsl|var\()/i.test(v)) return v;
  return `var(--mantine-color-${v}-6)`;
}

/** A colour from the API (a Mantine colour name or a hex value) safe to hand to a Badge. */
export const badgeColour = (c?: string | null, fallback = 'gray') => (c && c.trim() ? c.trim() : fallback);

// ---------------------------------------------------------------- welcome message

export const WELCOME_MERGE_FIELDS = ['[first name]', '[your name]', '[church]', '[service time]'];

/** The welcome message with its merge fields filled in (what the volunteer sends from their own WhatsApp). */
export function fillWelcome(template: string | null | undefined, values: { firstName: string; yourName: string; church: string; serviceTime: string }): string {
  return (template ?? '')
    .replace(/\[first name\]/gi, values.firstName)
    .replace(/\[your name\]/gi, values.yourName)
    .replace(/\[church\]/gi, values.church)
    .replace(/\[service time\]/gi, values.serviceTime);
}

// ---------------------------------------------------------------- intake channels

/** Fallback names when the lookups are still loading (the lookup table is the source of truth). */
const CHANNEL_NAMES: Record<number, string> = {
  [SALVATION_INTAKE_CHANNEL.TypedIn]: 'Typed in',
  [SALVATION_INTAKE_CHANNEL.DecisionCard]: 'CCMS form',
  [SALVATION_INTAKE_CHANNEL.PlanningCentreForm]: 'Planning Centre form',
  [SALVATION_INTAKE_CHANNEL.Anatomy]: 'Anatomy',
  [SALVATION_INTAKE_CHANNEL.LegacyApp]: 'Old app',
  [SALVATION_INTAKE_CHANNEL.PlanningCentreList]: 'Planning Centre list',
  [SALVATION_INTAKE_CHANNEL.Spreadsheet]: 'Spreadsheet',
};

export const channelFallbackName = (id: number) => CHANNEL_NAMES[id] ?? `Channel ${id}`;
